export type SessionAnalysisDisplayState =
  | { status: 'idle' }
  | {
      status: 'clean';
      summary?: string;
      findings?: string[];
    }
  | {
      status: 'needs_repair';
      summary?: string;
      findings?: string[];
      duplicateCount: number;
      gapCount: number;
    }
  | { status: 'failed'; message: string };

const buildEventIndicatorFromCounts = (
  duplicateCount: number,
  gapCount: number
) => {
  const hasGap = gapCount > 0;
  const hasDuplicate = duplicateCount > 0;

  if (hasGap && hasDuplicate) {
    return { color: 'orange', label: 'gap + duplicati' };
  }
  if (hasGap) {
    return { color: 'yellow', label: 'gap' };
  }
  if (hasDuplicate) {
    return { color: 'blue', label: 'duplicati' };
  }
  return { color: 'teal', label: 'nessun problema' };
};

export const getSessionEventBadge = ({
  analysis,
  isRepaired,
  isExcluded,
  isLoading,
}: {
  analysis?: SessionAnalysisDisplayState;
  isRepaired: boolean;
  isExcluded: boolean;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return {
      color: 'gray',
      label: 'Caricamento…',
      variant: 'light' as const,
    };
  }
  if (isExcluded) {
    return { color: 'gray', label: 'ESCLUSA', variant: 'outline' as const };
  }
  if (isRepaired) {
    return { color: 'teal', label: 'riparata', variant: 'light' as const };
  }
  if (analysis?.status === 'clean') {
    return {
      color: 'teal',
      label: 'Record perfetto',
      variant: 'light' as const,
    };
  }
  if (analysis?.status === 'needs_repair') {
    return {
      ...buildEventIndicatorFromCounts(
        analysis.duplicateCount,
        analysis.gapCount
      ),
      variant: 'outline' as const,
    };
  }
  if (analysis?.status === 'failed') {
    return {
      color: 'red',
      label: 'Analisi fallita',
      variant: 'light' as const,
    };
  }
  return {
    color: 'gray',
    label: 'Non analizzata',
    variant: 'light' as const,
  };
};

export const getSessionEventTooltip = (
  analysis: SessionAnalysisDisplayState | undefined,
  isExcluded: boolean,
  isLoading: boolean
) => {
  if (isLoading) {
    return 'Stiamo recuperando i dati aggiornati della sessione e verificando se esiste una riparazione salvata.';
  }
  if (isExcluded) {
    return 'Sessione esclusa dal ricalcolo.';
  }
  if (analysis?.status === 'clean') {
    return (
      analysis.summary ?? 'Nessun duplicato né gap rilevato sui dati originali.'
    );
  }
  if (analysis?.status === 'needs_repair') {
    return (
      analysis.findings?.join(' ') ??
      `${analysis.duplicateCount} duplicati, ${analysis.gapCount} gap rilevati.`
    );
  }
  if (analysis?.status === 'failed') {
    return analysis.message;
  }
  return "Esegui l'analisi per verificare gap e duplicati sui dati originali.";
};
